import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@material-ui/core/Button';
import { HighlightOff, Print, Save } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { getDirtyOrDefinedValues, getProjectMetrics } from './utils';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import CircularLoader from '../loader/CircularLoader';
import { addSnackbar } from '../../features/snackbar/actions';
import { reloadProject } from '../../features/project/actions';
import {
  Card,
  CardContent,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { upsertA3 } from '../../models/api/project-lifecycle';
import {
  GetPublicProjectResponse,
  IA3,
  PartialProjectRequestSchema,
} from '../../api-client/autogenerated';
import { FileWithPath, useDropzone } from 'react-dropzone';
import ImageIcon from '@material-ui/icons/Image';
import { uploadToStaging, verifyFileByUrl } from '../../models/api/filesystem';
import {
  FileDataUrl,
  getImageByFileId,
  readFileAsync,
  resizeImageHelper,
} from '../../scripts/utils';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import {
  ALL_FACILITY_OPTIONS,
  CONSTRUCTION_TYPE_OPTIONS,
  CONSULTANT_ROLES,
  CONTRACTOR_ROLES,
  DELIVERY_METHOD_OPTIONS,
  GRACE_ROLES,
  PROJECT_TYPE_OPTIONS,
} from './data';
import { useHistory } from 'react-router-dom';
import FormTextField from './components/FormTextField';
import FormDatePicker from './components/FormDatePicker';
import FormSelect from './components/FormSelect';
import AssignedUserText from '../custom-components/AssignedUserText';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    quillWrapper: {
      '& .quill': {
        backgroundColor: '#fff',
      },
      '& .ql-container': {
        minHeight: 150,
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
      },
      '& .ql-editor': {
        minHeight: 150,
      },
      '& .ql-editor.ql-blank::before': {
        fontStyle: 'normal',
        color: 'rgba(0, 0, 0, 0.38)',
      },
    },
    quillDisabled: {
      '& .ql-toolbar': {
        display: 'none',
      },
      '& .ql-container': {
        border: '1px solid rgba(0, 0, 0, 0.12)',
      },
      '& .ql-editor': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        cursor: 'not-allowed',
      },
    },
    imageContainer: {
      position: 'relative',
      width: '100%',
      height: 200,
      border: '2px dashed #ccc',
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: '#fafafa',
    },
    uploadedImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    deleteButton: {
      position: 'absolute',
      top: 4,
      right: 4,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)',
      },
    },
    sectionCard: {
      marginBottom: 24,
      boxShadow: 'none',
      border: '1px solid #EDECEC',
    },
    sectionTitleStyle: {
      background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
      borderRadius: '4px 4px 0px 0px',
      padding: '5px 16px',
    },
    sectionTitle: {
      color: '#FFFFFF !important',
      margin: 0,
      fontSize: '20px',
      fontWeight: 500,
    },
    sectionContent: {
      padding: '24px',
    },
  }),
);

interface UploadedFile {
  name: string;
  moveFromStagingImageKey: string;
  new: boolean;
}

type RichTextFieldName =
  | 'why'
  | 'currentState'
  | 'gapAnalysis'
  | 'targetState'
  | 'solutionApproach'
  | 'obstaclesInsights'
  | 'highlights'
  | 'impact';

const schema = z.object({
  // Project Metrics fields
  projectLocation: z.string().optional(),
  startDate: z.string().optional(),
  completionDate: z.string().optional(),
  estimatedCost: z.string().optional(),
  constructionCost: z.string().optional(),
  deliveryMethod: z.string().optional(),
  officeLocation: z.string().optional(),
  squareFootage: z.string().optional(),
  constructionType: z.string().optional(),
  keyTeamMembers: z.array(z.string()).optional(),

  // Rich text fields
  why: z.string().optional(),
  currentState: z.string().optional(),
  gapAnalysis: z.string().optional(),
  targetState: z.string().optional(),
  solutionApproach: z.string().optional(),
  obstaclesInsights: z.string().optional(),
  highlights: z.string().optional(),
  impact: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;
type SchemaInput = z.input<typeof schema>;

const defaultValues: Partial<FormValues> = {
  projectLocation: '',
  startDate: '',
  completionDate: '',
  estimatedCost: '',
  constructionCost: '',
  deliveryMethod: '',
  officeLocation: '',
  squareFootage: '',
  constructionType: '',
  keyTeamMembers: [],

  why: '',
  currentState: '',
  gapAnalysis: '',
  targetState: '',
  solutionApproach: '',
  obstaclesInsights: '',
  highlights: '',
  impact: '',
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'indent',
  'link',
];

interface Props {
  canEdit: boolean;
  isEditing: boolean;
  publicProject?: GetPublicProjectResponse | null;
}

type Option = {
  name: string;
  company: string;
  email: string;
  value: string;
};

type OptionGroup = {
  label: string;
  options: Option[];
};

function buildOptionsForRoles(
  roles: readonly { label: string; value: string }[],
  data: Record<string, any>,
): Option[] {
  return roles
    .map(({ label: roleLabel, value: roleValue }) => {
      const name = data[`${roleValue}Name`];
      const email = data[`${roleValue}Email`];
      const company = data[`${roleValue}Company`];

      if (!email) return null;

      return {
        name,
        email,
        company,
        value: roleValue,
      };
    })
    .filter((o): o is Option => Boolean(o));
}

function buildGroupedOptions(data?: PartialProjectRequestSchema | null): OptionGroup[] {
  if (!data) return [];

  const groups: OptionGroup[] = [];

  const graceOptions = buildOptionsForRoles(GRACE_ROLES, data);
  if (graceOptions.length) {
    groups.push({
      label: 'Grace Design Studios Roles',
      options: graceOptions,
    });
  }

  const contractorOptions = buildOptionsForRoles(CONTRACTOR_ROLES, data);
  if (contractorOptions.length) {
    groups.push({
      label: 'Contractor Roles',
      options: contractorOptions,
    });
  }

  const consultantOptions = buildOptionsForRoles(CONSULTANT_ROLES, data);
  if (consultantOptions.length) {
    groups.push({
      label: 'Consultant Roles',
      options: consultantOptions,
    });
  }

  return groups;
}

const parseData = (data: IA3): SchemaInput => {
  return {
    projectLocation: data?.projectLocation ?? '',
    deliveryMethod: data?.deliveryMethod ?? '',
    startDate: data?.startDate ?? '',
    completionDate: data?.completionDate ?? '',
    estimatedCost: data?.estimatedCost?.toString() ?? '',
    constructionCost: data?.constructionCost?.toString() ?? '',
    officeLocation: data?.officeLocation ?? '',
    squareFootage: data?.squareFootage?.toString() ?? '',
    keyTeamMembers: data?.keyTeamMembers ?? [],
    constructionType: data?.constructionType ?? '',

    // Rich text fields
    why: data?.why ?? '',
    currentState: data?.currentState ?? '',
    gapAnalysis: data?.gapAnalysis ?? '',
    targetState: data?.targetState ?? '',
    solutionApproach: data?.solutionApproach ?? '',
    obstaclesInsights: data?.obstaclesInsights ?? '',
    highlights: data?.highlights ?? '',
    impact: data?.impact ?? '',
  };
};

export default function A3Form(props: Props) {
  const { canEdit, isEditing, publicProject } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const project = useSelector(getProjectState);

  const publicData = publicProject?.projectA3;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting, dirtyFields, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    reValidateMode: 'onChange',
  });

  const [
    projectLocation,
    startDate,
    deliveryMethod,
    officeLocation,
    totalSquareFootage,
    keyTeamMembers,
    why,
    currentState,
    gapAnalysis,
    targetState,
    solutionApproach,
    obstaclesInsights,
    highlights,
    impact,
  ] = watch([
    'projectLocation',
    'startDate',
    'deliveryMethod',
    'officeLocation',
    'squareFootage',
    'keyTeamMembers',
    'why',
    'currentState',
    'gapAnalysis',
    'targetState',
    'solutionApproach',
    'obstaclesInsights',
    'highlights',
    'impact',
  ]);

  const isMobile = useMediaQuery('(max-width: 800px)');
  const isFormDisabled = !isEditing || !canEdit;

  // Image upload states
  const [images, setImages] = useState<FileDataUrl[]>(['', '', '']);
  const [uploadedFiles, setUploadedFiles] = useState<(UploadedFile | null)[]>([null, null, null]);
  const [deletedImages, setDeletedImages] = useState<boolean[]>([false, false, false]);
  const [isUploadingImages, setIsUploadingImages] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  const fillFormFieldsFromProject = () => {
    const projectLocation = project?.projectRequest?.data?.projectLocation;
    if (projectLocation) {
      setValue('projectLocation', projectLocation);
    }

    const startDate = project?.projectRequest?.data?.startDate || project?.noticeToProceedDate;
    if (startDate) {
      setValue('startDate', startDate);
    }

    const completionDate =
      project?.projectRequest?.data?.targetCompletionDate || project?.substantialCompletionDate;
    if (completionDate) {
      setValue('completionDate', completionDate);
    }

    const estimatedCost = project?.projectRequest?.data?.projectBudget;
    if (estimatedCost) {
      setValue('estimatedCost', estimatedCost);
    }

    const constructionCost =
      project?.projectRequest?.data?.constructionBudget || project?.contractAmount?.toString();
    if (constructionCost) {
      setValue('constructionCost', constructionCost);
    }

    const deliveryMethod = project?.projectRequest?.data?.deliveryMethod;
    if (deliveryMethod) {
      setValue('deliveryMethod', deliveryMethod);
    }

    const squareFootage = project?.projectRequest?.data?.totalSquareFootage;
    if (squareFootage) {
      setValue('squareFootage', squareFootage);
    }
  };

  const loadImages = async (data: IA3) => {
    const loadImage = async (fileId: string, index: number) => {
      try {
        const img = await getImageByFileId(fileId, false);
        setImages((prev) => {
          const newImages = [...prev];
          newImages[index] = img;
          return newImages;
        });
      } catch (error) {
        console.error(`Error loading image ${index}:`, error);
      }
    };

    setIsLoadingImages(true);
    const promises: Promise<void>[] = [];

    if (data.image1FileId) {
      promises.push(loadImage(data.image1FileId, 0));
    }
    if (data.image2FileId) {
      promises.push(loadImage(data.image2FileId, 1));
    }
    if (data.image3FileId) {
      promises.push(loadImage(data.image3FileId, 2));
    }

    try {
      await Promise.all(promises);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const getDeliveryMethod = () => {
    if (deliveryMethod === 'other') {
      return project?.projectRequest?.data?.deliveryMethodOther || '';
    }

    return deliveryMethod;
  };

  const getProjectType = () => {
    if (!project?.projectRequest?.data?.projectType) return '';

    if (project.projectRequest.data.projectType === 'other') {
      return project.projectRequest.data.projectTypeOther || '';
    }

    return (
      PROJECT_TYPE_OPTIONS.find((x) => x.value === project?.projectRequest?.data?.projectType)
        ?.label || ''
    );
  };

  const getFacilityTypes = () => {
    if (
      !project?.projectRequest?.data?.facilityTypes ||
      project.projectRequest.data.facilityTypes.length === 0
    )
      return '';

    return project.projectRequest.data.facilityTypes
      .map((x) => ALL_FACILITY_OPTIONS.find((y) => y.value === x)?.label || '')
      .join(', ');
  };

  const handleUploadProgress = (event: any) => {
    setUploadProgress(Math.round((100 * event.loaded) / event.total));
  };

  const onDrop = async (acceptedFiles: (FileWithPath | File)[], imageIndex: number) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    try {
      setIsUploadingImages(true);
      dispatch(blockNavigation());

      const resizedImage = await resizeImageHelper(file);
      const response = await uploadToStaging(
        {
          fileName: resizedImage.name,
          useMultiPartUpload: resizedImage.size > MULTI_PART_FILE_SIZE,
        },
        resizedImage,
        handleUploadProgress,
      );

      if (!response) {
        throw new Error('Upload failed');
      }

      const verified = await verifyFileByUrl(response.s3Key);
      if (!verified) {
        throw new Error('File verification failed');
      }

      const dataUrl = await readFileAsync(resizedImage);

      setImages((prev) => {
        const newImages = [...prev];
        newImages[imageIndex] = dataUrl;
        return newImages;
      });

      setUploadedFiles((prev) => {
        const newFiles = [...prev];
        newFiles[imageIndex] = {
          moveFromStagingImageKey: response.s3Key,
          name: file.name,
          new: true,
        };
        return newFiles;
      });

      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Image uploaded successfully',
          severity: 'success',
        }),
      );
    } catch (error: any) {
      console.error(`Error uploading file: ${error.message}`);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Image upload failed. Please try again.',
          severity: 'error',
        }),
      );
    } finally {
      setIsUploadingImages(false);
      dispatch(allowNavigation());
    }
  };

  const deleteUploadedImageFile = async (imageIndex: number) => {
    const file = images[imageIndex];
    if (!file) return;

    try {
      setDeletedImages((prev) => {
        const newDeleted = [...prev];
        newDeleted[imageIndex] = true;
        return newDeleted;
      });

      setImages((prev) => {
        const newImages = [...prev];
        newImages[imageIndex] = '';
        return newImages;
      });

      setUploadedFiles((prev) => {
        const newFiles = [...prev];
        newFiles[imageIndex] = null;
        return newFiles;
      });

      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Image will be deleted when you save',
          severity: 'info',
        }),
      );
    } catch (error) {
      console.error('Error marking image for deletion:', error);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Failed to mark image for deletion',
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    if (publicData) {
      reset(parseData(publicData));
      loadImages(publicData);
    } else if (project?.projectA3) {
      reset(parseData(project.projectA3));
      loadImages(project.projectA3);
    } else {
      fillFormFieldsFromProject();
    }
  }, [project?.projectA3, publicData]);

  useEffect(() => {
    if (isDirty) {
      dispatch(blockNavigation());
    } else {
      dispatch(allowNavigation());
    }
  }, [isDirty]);

  const groupedOptions = useMemo(
    () => buildGroupedOptions(project?.projectRequest?.data || publicProject?.projectRequest?.data),
    [project?.projectRequest?.data, publicProject?.projectRequest?.data],
  );

  const onSubmit = handleSubmit(
    async (data) => {
      await submit(data);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'A3 Summary saved successfully',
          severity: 'success',
        }),
      );
      return;
    },
    (errors) => {
      console.log('errors', errors);
    },
  );

  const submit = async (data: FormValues) => {
    if (!project) return;

    const dirtyOrDefinedValues = getDirtyOrDefinedValues<FormValues>(dirtyFields, data);

    const buildImageAction = (index: number) => {
      const hasNewUpload = uploadedFiles[index]?.new;
      const isDeleted = deletedImages[index];

      if (hasNewUpload) {
        return {
          upload: true,
          remove: isDeleted,
          moveFromStagingImageKey: uploadedFiles[index]!.moveFromStagingImageKey,
          name: uploadedFiles[index]!.name,
        };
      }

      if (isDeleted) {
        return {
          remove: true,
        };
      }

      return undefined;
    };

    const { constructionCost, estimatedCost, squareFootage, ...rest } = dirtyOrDefinedValues;

    const submissionData: any = {
      ...rest,
      ...(constructionCost !== undefined && constructionCost !== null && constructionCost !== ''
        ? { constructionCost: Number(constructionCost) }
        : {}),
      ...(estimatedCost !== undefined && estimatedCost !== null && estimatedCost !== ''
        ? { estimatedCost: Number(estimatedCost) }
        : {}),
      ...(squareFootage !== undefined && squareFootage !== null && squareFootage !== ''
        ? { squareFootage: Number(squareFootage) }
        : {}),
    };

    const image1Action = buildImageAction(0);
    const image2Action = buildImageAction(1);
    const image3Action = buildImageAction(2);

    if (image1Action) submissionData.image1 = image1Action;
    if (image2Action) submissionData.image2 = image2Action;
    if (image3Action) submissionData.image3 = image3Action;

    await upsertA3(project.id, submissionData);

    reset(data);

    dispatch(allowNavigation());
    dispatch(reloadProject());
  };

  const projectName =
    project?.projectRequest?.data?.projectName || project?.name || publicProject?.projectName || '';
  const clientName = project?.projectRequest?.data?.clientName || '';
  const projectNumber = project?.projectRequest?.data?.projectNumber || '';
  const newSquareFootage = project?.projectRequest?.data?.newSquareFootage;
  const renovatedSquareFootage = project?.projectRequest?.data?.renovatedSquareFootage;

  const handlePrint = () => {
    const url = publicProject?.projectKey
      ? `/main/public/projects/${publicProject.projectKey}/a3/print`
      : `/main/projects/${project?.id}/a3/print`;
    history.push(url, {
      row1Html: [
        getProjectMetrics({
          projectNumber,
          projectLocation,
          officeLocation,
          deliveryMethod: getDeliveryMethod(),
          startDate,
          renovatedSquareFootage,
          newSquareFootage,
          totalSquareFootage,
        }),
        obstaclesInsights!,
        highlights!,
      ],
      row2Images: [{ src: images[0] }, { src: images[1] }, { src: images[2] }],
      row3Html: [why!, currentState!, solutionApproach!],
      row4Html: [gapAnalysis!, targetState!, impact!],
      clientName,
      projectName,
      projectType: getProjectType(),
      facilityTypes: getFacilityTypes(),
    });
  };

  const renderRichTextField = (name: RichTextFieldName) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className={`${classes.quillWrapper} ${isFormDisabled ? classes.quillDisabled : ''}`}>
          <ReactQuill
            theme="snow"
            value={field.value || ''}
            onChange={field.onChange}
            modules={quillModules}
            formats={quillFormats}
            readOnly={isFormDisabled}
            style={{ textTransform: 'none', fontFamily: 'Roboto' }}
          />
        </div>
      )}
    />
  );

  const ImageUploadSlot = ({ index }: { index: number }) => {
    const { getRootProps, getInputProps } = useDropzone({
      accept: 'image/jpeg, image/png',
      multiple: false,
      onDrop: (files) => onDrop(files, index),
      disabled: isFormDisabled,
    });

    return (
      <div className={classes.imageContainer}>
        {images[index] ? (
          <>
            <img
              src={images[index]?.toString()}
              alt={`Upload ${index + 1}`}
              className={classes.uploadedImage}
            />
            {!isFormDisabled && (
              <IconButton
                className={classes.deleteButton}
                onClick={() => deleteUploadedImageFile(index)}
                size="small"
              >
                <HighlightOff />
              </IconButton>
            )}
          </>
        ) : (
          <div
            {...getRootProps()}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isFormDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            <input {...getInputProps()} />
            <ImageIcon style={{ fontSize: 48, color: '#999', marginBottom: 8 }} />
            <Typography variant="body2" color="textSecondary">
              Click or drag to upload
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Image {index + 1}
            </Typography>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<Print />}
        disabled={isLoadingImages}
        onClick={handlePrint}
      >
        Print
      </Button>
      <form noValidate onSubmit={onSubmit} style={{ marginTop: 24 }}>
        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Project Metrics</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? 16 : 24,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Location
                </Typography>
                <FormTextField disabled={isFormDisabled} name="projectLocation" control={control} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Start Date
                </Typography>
                <FormDatePicker disabled={isFormDisabled} name="startDate" control={control} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Completion Date
                </Typography>
                <FormDatePicker disabled={isFormDisabled} name="completionDate" control={control} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Estimated Cost
                </Typography>
                <FormTextField
                  disabled={isFormDisabled}
                  name="estimatedCost"
                  control={control}
                  prefix="$"
                  signedNumericOnly
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Construction Cost
                </Typography>
                <FormTextField
                  disabled={isFormDisabled}
                  name="constructionCost"
                  control={control}
                  prefix="$"
                  signedNumericOnly
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Delivery Method
                </Typography>
                <FormSelect
                  name="deliveryMethod"
                  control={control}
                  disabled={isFormDisabled}
                  options={[
                    ...DELIVERY_METHOD_OPTIONS.slice(0, -1),
                    ...(project?.projectRequest?.data?.deliveryMethodOther
                      ? [
                          {
                            value: 'other',
                            label: project?.projectRequest?.data?.deliveryMethodOther || '',
                          },
                        ]
                      : []),
                  ]}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Office Location
                </Typography>
                <FormTextField disabled={isFormDisabled} name="officeLocation" control={control} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Square Footage
                </Typography>
                <FormTextField
                  disabled={isFormDisabled}
                  name="squareFootage"
                  control={control}
                  signedNumericOnly
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Construction Type
                </Typography>
                <FormSelect
                  name="constructionType"
                  control={control}
                  disabled={isFormDisabled}
                  options={CONSTRUCTION_TYPE_OPTIONS}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#757575',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Key team members
                </Typography>
                <Controller
                  render={({ field }) => {
                    return (
                      <FormControl size="small" variant="outlined" disabled={isFormDisabled}>
                        <Select
                          multiple
                          value={field.value}
                          onChange={(event) => {
                            const arr = event.target.value as string[];
                            const values = Array.isArray(arr) ? arr : [arr];
                            field.onChange([...values]);
                          }}
                          displayEmpty
                          renderValue={(selected) => {
                            const selectedArray = selected as string[];
                            return selectedArray?.length > 0
                              ? `${selectedArray.length} members selected`
                              : 'Click to select key team members';
                          }}
                          // @ts-ignore
                          MenuProps={{ getContentAnchorEl: () => null, style: { maxHeight: 400 } }}
                        >
                          {groupedOptions.length === 0 && (
                            <MenuItem disabled>
                              <em>No options available</em>
                            </MenuItem>
                          )}

                          {groupedOptions.flatMap((group) => [
                            <MenuItem
                              key={`${group.label}-header`}
                              disabled
                              style={{
                                paddingTop: 12,
                                paddingBottom: 12,
                                fontWeight: 500,
                                opacity: 1,
                              }}
                            >
                              {group.label}
                            </MenuItem>,
                            ...group.options.map((option) => {
                              const isSelected = keyTeamMembers?.includes(option.value);

                              return (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                  style={{
                                    // backgroundColor: isSelected ? '#005cbf' : undefined,
                                    color: isSelected ? '#fff' : undefined,
                                  }}
                                >
                                  <Checkbox checked={isSelected} />
                                  <AssignedUserText
                                    name={option.name + ` (${option.email})`}
                                    company={option.company}
                                  />
                                </MenuItem>
                              );
                            }),
                          ])}
                        </Select>
                      </FormControl>
                    );
                  }}
                  control={control}
                  name="keyTeamMembers"
                />
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <Typography
                variant="caption"
                style={{
                  color: '#757575',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Project Images
              </Typography>
              {isUploadingImages && (
                <div style={{ marginBottom: 16 }}>
                  <CircularLoader />
                  <Typography align="center" style={{ marginTop: 8 }}>
                    Uploading... {uploadProgress}%
                  </Typography>
                </div>
              )}
              <Grid style={{ marginTop: -3 }} container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <ImageUploadSlot index={0} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ImageUploadSlot index={1} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ImageUploadSlot index={2} />
                </Grid>
              </Grid>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Why</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Client Strategic Goals</li>
                  <li>Client / Community Needs</li>
                  <li>Problem the Project Solves</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('why')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Current State</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Existing Conditions</li>
                  <li>Current Operational Model</li>
                  <li>Key Pain Points</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('currentState')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Gap Analysis</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Demographics</li>
                  <li>Growth Requirements</li>
                  <li>Missed Opportunities</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('gapAnalysis')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Target State</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Critical Program Elements / Adjacencies</li>
                  <li>Target Operational Models</li>
                  <li>Functional & Aesthetic Drivers</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('targetState')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Solution Approach</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Design Strategies</li>
                  <li>Consultant & Contractor Coordination</li>
                  <li>Technology or Innovation Employed</li>
                  <li>Collaboration Approaches</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('solutionApproach')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Obstacles and Insights</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Project Challenges</li>
                  <li>Approach Taken</li>
                  <li>Insights or Learnings</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('obstaclesInsights')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Highlights</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Specialized Design Elements</li>
                  <li>Innovations</li>
                  <li>Certifications</li>
                  <li>Awards / Recognition</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('highlights')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionTitleStyle}>
            <h2 className={classes.sectionTitle}>Impact</h2>
          </CardContent>

          <CardContent className={classes.sectionContent}>
            <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: '0 0 calc(30% - 24px)' }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
                  <li>Client Measures of Success</li>
                  <li>Community Impact</li>
                  <li>Implementation Results</li>
                  <li>Ongoing & Future Success Metrics</li>
                </ul>
              </div>
              <div style={{ flex: '0 0 70%' }}>{renderRichTextField('impact')}</div>
            </div>
          </CardContent>
        </Card>

        {isEditing ? (
          <div
            style={{
              position: isMobile ? 'static' : 'sticky',
              bottom: 0,
              width: 'calc(100% + 32px)',
              padding: 16,
              marginLeft: -16,
              backgroundColor: '#FFF',
              borderTop: '1px solid #CCC',
              zIndex: 100,
            }}
          >
            {!isSubmitting ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!canEdit}
                startIcon={<Save />}
              >
                Save Changes
              </Button>
            ) : (
              <CircularLoader style={{ justifyContent: 'start', paddingLeft: 32 }} />
            )}
          </div>
        ) : null}
      </form>
    </>
  );
}
